/**
 * Esquemas zod compartidos por la web y la app. Replican el contrato OpenAPI
 * del backend para poder validar en cliente antes de enviar nada por la red.
 */
import { z } from 'zod';
import { daysInMonth } from './nutrition/tdee.js';
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function isRealCalendarDate(value) {
    const match = ISO_DATE_RE.exec(value);
    if (!match)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        return false;
    return day >= 1 && day <= daysInMonth(year, month);
}
/** Fecha local de `date` en formato `YYYY-MM-DD`. */
function toIsoDate(date) {
    const y = String(date.getFullYear()).padStart(4, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Cadena `YYYY-MM-DD` que corresponde a una fecha real del calendario. */
export const isoDateSchema = z
    .string()
    .regex(ISO_DATE_RE, 'debe tener formato YYYY-MM-DD')
    .refine(isRealCalendarDate, 'no es una fecha del calendario');
/** Instante ISO 8601; se admite tanto `Z` como un desplazamiento `±HH:MM`. */
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
/** UUID en cualquiera de sus versiones. */
export const uuidSchema = z.uuid();
export const sexSchema = z.enum(['male', 'female', 'other']);
export const activityLevelSchema = z.enum([
    'sedentary',
    'light',
    'moderate',
    'active',
    'very_active',
]);
export const goalSchema = z.enum(['lose', 'maintain', 'gain']);
export const setTypeSchema = z.enum(['normal', 'warmup', 'failure', 'drop']);
/** Fecha de nacimiento: fecha real del calendario y nunca futura. */
export const birthDateSchema = isoDateSchema.refine((value) => value <= toIsoDate(new Date()), 'no puede estar en el futuro');
/**
 * Cuerpo de `PUT /v1/me/profile`. Todos los campos escalares son opcionales y
 * anulables (el perfil se reemplaza entero) y las listas omitidas quedan vacías.
 */
export const profileInputSchema = z.object({
    sex: sexSchema.nullish(),
    birthDate: birthDateSchema.nullish(),
    heightCm: z.number().min(50).max(260).nullish(),
    activityLevel: activityLevelSchema.nullish(),
    goal: goalSchema.nullish(),
    dietaryPreferences: z.array(z.string()).default([]),
    allergies: z.array(z.string()).default([]),
    equipment: z.array(z.string()).default([]),
    trainingDaysPerWeek: z.number().int().min(0).max(7).nullish(),
});
/** Alta de cuenta: `POST /v1/auth/register`. */
export const registerSchema = z.object({
    email: z.email().max(254),
    password: z.string().min(8).max(128),
    displayName: z.string().min(1).max(60),
    handle: z.string().regex(/^[a-z0-9_]{3,30}$/, 'sólo minúsculas, dígitos y guion bajo (3-30)'),
    device: z.string().max(100).optional(),
    /**
     * Código de invitación. Sólo lo exige un servidor con el registro cerrado
     * (`REGISTRATION_INVITE_CODE`); con el registro abierto se ignora. Se recortan
     * los espacios, igual que hace el servidor, y vacío equivale a no enviarlo.
     */
    inviteCode: z.string().trim().max(128).optional(),
});
/** Inicio de sesión: `POST /v1/auth/login`. */
export const loginSchema = z.object({
    email: z.email().max(254),
    password: z.string().min(1).max(128),
    device: z.string().max(100).optional(),
});
/**
 * Cambio de contraseña: `POST /v1/me/password`. La nueva sigue las reglas del
 * registro; que sea distinta de la actual lo comprueba también el servidor.
 */
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1).max(128),
    newPassword: z.string().min(8).max(128),
    device: z.string().max(100).optional(),
})
    .refine((value) => value.newPassword !== value.currentPassword, {
    path: ['newPassword'],
    message: 'La contraseña nueva tiene que ser distinta de la actual.',
});
/** Borrado de la cuenta: `DELETE /v1/me`, confirmado con la contraseña. */
export const deleteAccountSchema = z.object({
    password: z.string().min(1).max(128),
});
/**
 * Serie registrada en el diario de entrenamiento. `clientId` es el UUID que
 * genera el cliente y que la cola de sincronización usa como identificador
 * idempotente de la mutación.
 */
export const setLogInputSchema = z.object({
    clientId: uuidSchema,
    exerciseId: uuidSchema,
    setNumber: z.number().int().min(1),
    reps: z.number().int().min(0).max(200),
    weightKg: z.number().min(0).max(1000),
    rpe: z.number().min(1).max(10).multipleOf(0.5).optional(),
    type: setTypeSchema,
    performedAt: isoDateTimeSchema,
});
/** Medición corporal: `POST /v1/me/body-metrics`. */
export const bodyMetricInputSchema = z.object({
    measuredAt: isoDateTimeSchema.optional(),
    weightKg: z.number().min(0).max(500).optional(),
    bodyFatPct: z.number().min(0).max(100).optional(),
    measurements: z.record(z.string(), z.number()).optional(),
    note: z.string().max(500).optional(),
});
//# sourceMappingURL=schemas.js.map